import { createHandlerChainRunner, markSkipOnCompleteIfOnSuccessIsSet } from "@insite/client-framework/HandlerCreator";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    DispatchBeginReorder,
    DispatchCompleteReorder,
    FetchOrderWithLines,
    LoadCart,
    PopulateApiParameter,
    SendDataToApi,
} from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/Reorder";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import { getDataLayer } from "@waytek/Handlers/Utilities/CustomTracking";

type CartLineData = Pick<
    CartLineModel,
    "shortDescription" | "erpNumber" | "pricing" | "qtyOrdered" | "properties" | "brand"
>;

export const XngGoogleAnalyticsReorder: typeof DispatchBeginReorder = props => {
    const { apiParameter, getState, apiResult } = props as any;
    const dataLayer = getDataLayer();
    const state = getState();
    const session = getSession(state);
    if (!dataLayer) {
        return;
    }

    let cartLinesToAdd: CartLineData[] = [];
    if (props.apiResult?.cartLines || apiParameter?.cartLineCollection?.cartLines) {
        // XNG-Changes: this if condition is for Order History Reorder functionality
        if ((props.apiResult?.cartLines as any)?.cartLines) {
            cartLinesToAdd = (props.apiResult?.cartLines as any)?.cartLines || [];
        } else {
            cartLinesToAdd = props.apiResult?.cartLines || apiParameter.cartLineCollection?.cartLines || [];
        }
    } else {
        cartLinesToAdd = [
            {
                shortDescription: apiResult.shortDescription || "",
                erpNumber: apiResult.erpNumber || "",
                pricing: apiResult.pricing || null,
                qtyOrdered: apiResult.qtyOrdered || null,
                properties: apiResult.properties,
                brand: apiResult.brand,
            },
        ];
    }

    const totalPrice =
        cartLinesToAdd.length > 1
            ? cartLinesToAdd.reduce((total, line) => {
                  if (line.pricing && line.pricing.extendedUnitNetPrice) {
                      return total + line.pricing.extendedUnitNetPrice;
                  }
                  return total;
              }, 0)
            : cartLinesToAdd[0].pricing?.unitNetPrice;

    const googleAnalyticsData = {
        event: "add_to_cart",
        ecommerce: {
            currency: state.context.session.currency?.currencyCode,
            value: totalPrice,
            element_id: "Reorder",
            ...(apiResult?.pricing && { value: apiResult.pricing.extendedUnitNetPrice }),
            items: cartLinesToAdd.map((cartLine, idx) => ({
                item_id: cartLine.erpNumber,
                item_name: cartLine.shortDescription,
                discount: cartLine.pricing
                    ? cartLine.pricing.unitNetPrice < cartLine.pricing.unitListPrice
                        ? cartLine.pricing.unitListPrice - cartLine.pricing.unitNetPrice
                        : 0
                    : 0,
                index: idx,
                item_brand: (cartLine as CartLineModel).brand?.name || "",
                item_category: cartLine.properties?.["item_category"] || "",
                item_category2: cartLine.properties?.["item_category2"] || "",
                item_category3: cartLine.properties?.["item_category3"] || "",
                item_category4: cartLine.properties?.["item_category4"] || "",
                price: cartLine.pricing?.unitNetPrice,
                quantity: cartLine.qtyOrdered,
                item_list_id: cartLine.properties?.["categoryId"] || "",

                location_id: session.pickUpWarehouse?.id,
            })),
        },
    };

    dataLayer.push({ ecommerce: null });
    dataLayer.push(googleAnalyticsData);
};

export const ExecuteOnSuccessCallback: typeof DispatchBeginReorder = props => {
    markSkipOnCompleteIfOnSuccessIsSet(props);
    props.parameter.onSuccess?.();
};

export const chain = [
    DispatchBeginReorder,
    FetchOrderWithLines,
    PopulateApiParameter,
    SendDataToApi,
    ExecuteOnSuccessCallback,
    LoadCart,
    DispatchCompleteReorder,
    // XNG-Changes
    XngGoogleAnalyticsReorder,
];

const reorder = createHandlerChainRunner(chain, "Reorder");
export default reorder;
